/**
 * Увольнение по инициативе работодателя (объединённая форма HR «İşçini işdən çıxarma tələbi FM»,
 * ответы HR 30.10.2026). Подаёт прямой руководитель: выбирает сотрудника, категорию (от неё —
 * критерии оценки), причину, оценки 1–5 и предлагаемую дату. Статью ТК АР и согласованную дату
 * ставит HR на своём шаге. Право подать и блокеры 1С проверяет сервер — форма их показывает.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Text, XStack, YStack } from 'tamagui'

import { api } from '../../../api/client'
import { useApp } from '../../../i18n'
import type { Profile, RequestCard } from '../../../lib/types'
import { Button, DateField, FormTextArea, OptionSelect, todayYmd } from '../../../ui'
import { useMe } from '../../users/useUsers'
import { errorText } from '../errorText'
import {
  EMPLOYER_CATEGORIES, EMPLOYER_REASONS, SCORE_SCALE, criteriaFor, employerFormMissing, scoreSum,
  type EmployerCategory, type EmployerTerminationInput, type EmployerTerminationPayload,
} from '../termination-types'
import { PersonPickField } from './PersonPickField'
import { Field, FormActions } from './shared'

type Person = Pick<Profile, 'id' | 'fullName' | 'jobTitle' | 'avatarUrl'>
interface FormState {
  category?: EmployerCategory
  reasonCode?: string
  reasonOther: string
  scores: Record<string, number>
  notes: string
  proposedDate: string
}

export function TerminationEmployerForm({ card, onDone }: { card?: RequestCard; onDone: () => void }) {
  const { t } = useApp()
  const qc = useQueryClient()
  const { data: me } = useMe()
  const saved = card?.payload as unknown as EmployerTerminationPayload | undefined
  const [employee, setEmployee] = useState<Person | null>(
    saved ? { id: saved.employeeId, fullName: saved.employeeName ?? '', jobTitle: saved.employeePosition ?? null, avatarUrl: null } : null,
  )
  const [f, setF] = useState<FormState>({
    category: saved?.category,
    reasonCode: saved?.reasonCode,
    reasonOther: saved?.reasonOther ?? '',
    scores: saved?.scores ?? {},
    notes: saved?.notes ?? '',
    proposedDate: saved?.proposedDate ?? '',
  })
  const [requestId, setRequestId] = useState(card?.id)
  const refs = useQuery({
    queryKey: ['termination-employer-references', employee?.id],
    queryFn: () => api.terminationEmployerReferences({ employeeId: employee!.id }),
    enabled: !!employee,
    retry: false,
  })

  const input = (): EmployerTerminationInput => ({
    category: f.category,
    reasonCode: f.reasonCode,
    reasonOther: f.reasonOther.trim(),
    scores: f.scores,
    notes: f.notes.trim(),
    proposedDate: f.proposedDate,
  })
  // Первое сохранение создаёт черновик; дальше — правка по id, сотрудник уже закреплён за заявкой.
  const persist = async () => {
    const req = requestId
      ? await api.saveTerminationEmployer(requestId, input())
      : await api.createTerminationEmployerDraft({ employeeId: employee!.id, ...input() })
    setRequestId(req.id)
    return req
  }
  const refresh = (id: string) => {
    qc.invalidateQueries({ queryKey: ['edo-request', id] })
    qc.invalidateQueries({ queryKey: ['edo-requests'] })
  }
  const save = useMutation({ mutationFn: persist, onSuccess: (req) => refresh(req.id) })
  const submit = useMutation({
    mutationFn: async () => api.submitTermination((await persist()).id),
    onSuccess: (req) => {
      refresh(req.id)
      qc.invalidateQueries({ queryKey: ['edo-tasks'] })
      onDone()
    },
  })

  const criteria = f.category ? criteriaFor(f.category) : []
  const missing = employerFormMissing({ employeeId: employee?.id, ...f }).map((m) => t(m))
  const busy = save.isPending || submit.isPending
  const canSubmit = !!refs.data?.canSubmit && !missing.length && !busy
  const scoreOptions = SCORE_SCALE.map((s) => ({ value: String(s.value), label: t(s.label) }))
  const setScore = (key: string, value?: string) =>
    setF((prev) => ({ ...prev, scores: { ...prev.scores, [key]: Number(value) } }))

  return (
    <YStack gap={12}>
      <Text fontWeight="700">{t({ ru: 'Увольнение по инициативе работодателя', en: "Dismissal at the employer's initiative", az: 'İşəgötürənin təşəbbüsü ilə işdən çıxarma' })}</Text>
      <Text color="$muted">
        {t({
          ru: 'Заявка пройдёт: руководитель подразделения → HR (проверка, статья ТК АР, согласованная дата) → CEO. После решения CEO HR оформит приказ в 1С. Сотрудник заявку не видит.',
          en: 'Route: head of unit → HR (checks, Labour Code article, agreed date) → CEO. After the CEO decision HR issues the order in 1C. The employee does not see the request.',
          az: 'Marşrut: bölmə rəhbəri → HR (yoxlama, ƏM maddəsi, razılaşdırılmış tarix) → CEO. CEO qərarından sonra HR 1C-də əmr hazırlayır. İşçi müraciəti görmür.',
        })}
      </Text>

      {card || requestId ? (
        <Text>{t({ ru: 'Сотрудник', en: 'Employee', az: 'İşçi' })}: {employee?.fullName || '—'}</Text>
      ) : (
        <PersonPickField
          label={{ ru: 'Сотрудник', en: 'Employee', az: 'İşçi' }}
          hint={{ ru: 'Только ваш прямой подчинённый — это проверит сервер', en: 'Only your direct report — the server checks it', az: 'Yalnız birbaşa tabeçiliyinizdə olan işçi' }}
          value={employee}
          onChange={setEmployee}
          excludeIds={me?.id ? [me.id] : []}
        />
      )}
      {!!refs.data && (
        <Text color="$muted">
          {[refs.data.employee.position, refs.data.employee.companyName].filter(Boolean).join(' · ')}
        </Text>
      )}
      {refs.data?.blockers.map((b, i) => <Text key={i} color="$red10">{b}</Text>)}
      {refs.error instanceof Error && <Text color="$red10">{t(errorText(refs.error))}</Text>}

      <Field label={t({ ru: 'Категория сотрудника', en: 'Employee category', az: 'İşçinin kateqoriyası' })} required>
        <OptionSelect
          options={EMPLOYER_CATEGORIES.map((c) => ({ value: c.key, label: t(c.label) }))}
          value={f.category ? [f.category] : []}
          // Критерии у категорий разные — оценки начинаются заново.
          onChange={(next) => setF((prev) => ({ ...prev, category: next[0] as EmployerCategory | undefined, scores: {} }))}
        />
      </Field>

      {criteria.length > 0 && (
        <YStack gap={8}>
          <Text fontSize={12} fontWeight="600" color="$muted">
            {t({ ru: 'Оценка по критериям: 5 — полностью соответствует, 1 — совсем не соответствует', en: 'Evaluation: 5 — fully meets, 1 — not at all', az: 'Qiymətləndirmə: 5 — tam uyğundur, 1 — heç uyğun deyil' })}
          </Text>
          {criteria.map((c) => (
            <XStack key={c.key} gap={10} alignItems="center" flexWrap="wrap">
              <Text flex={1} minWidth={180} fontSize={13}>{t(c.label)}</Text>
              <YStack width={240}>
                <OptionSelect
                  options={scoreOptions}
                  value={f.scores[c.key] ? [String(f.scores[c.key])] : []}
                  placeholder="—"
                  onChange={(next) => setScore(c.key, next[0])}
                />
              </YStack>
            </XStack>
          ))}
          <Text fontWeight="600">
            {t({ ru: 'Сумма баллов', en: 'Total score', az: 'Cəmi bal' })}: {scoreSum(f.scores, f.category!)} {t({ ru: 'из', en: 'of', az: '/' })} {criteria.length * 5}
          </Text>
        </YStack>
      )}

      <Field label={t({ ru: 'Причина', en: 'Reason', az: 'Səbəb' })} required>
        <OptionSelect
          options={EMPLOYER_REASONS.map((r) => ({ value: r.key, label: t(r.label) }))}
          value={f.reasonCode ? [f.reasonCode] : []}
          placeholder={t({ ru: 'Выберите причину', en: 'Choose a reason', az: 'Səbəbi seçin' })}
          onChange={(next) => setF((prev) => ({ ...prev, reasonCode: next[0] }))}
        />
      </Field>
      {f.reasonCode === 'other' && (
        <Field label={t({ ru: 'Пояснение причины', en: 'Reason details', az: 'Səbəbin izahı' })} required>
          <FormTextArea value={f.reasonOther} maxLength={1000} onChangeText={(v) => setF((prev) => ({ ...prev, reasonOther: v }))} />
        </Field>
      )}
      <Field label={t({ ru: 'Примечание', en: 'Notes', az: 'Əlavə qeydlər' })}>
        <FormTextArea value={f.notes} maxLength={4000} onChangeText={(v) => setF((prev) => ({ ...prev, notes: v }))} />
      </Field>
      <Field label={t({ ru: 'Предлагаемая дата увольнения', en: 'Proposed dismissal date', az: 'Təklif edilən işdən azad olunma tarixi' })} required>
        <DateField value={f.proposedDate} min={todayYmd()} disabled={busy} onChange={(v) => setF((prev) => ({ ...prev, proposedDate: v }))} />
      </Field>

      {save.isSuccess && <Text>{t({ ru: 'Черновик сохранён', en: 'Draft saved', az: 'Qaralama saxlanıldı' })}</Text>}
      {save.error instanceof Error && <Text color="$red10">{t(errorText(save.error))}</Text>}
      <FormActions
        m={submit}
        ok={canSubmit}
        onCancel={onDone}
        onSubmit={() => canSubmit && submit.mutate()}
        missing={missing}
        submitLabel={t({ ru: 'Отправить на согласование', en: 'Submit for approval', az: 'Razılaşdırmaya göndər' })}
        extra={
          <Button disabled={!employee || busy} onPress={() => save.mutate()}>
            {t({ ru: 'Сохранить черновик', en: 'Save draft', az: 'Qaralamanı saxla' })}
          </Button>
        }
      />
    </YStack>
  )
}
